/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState} from 'react';
import LogsLayout from './LogsLayout';
import {
  EmptyState,
  Loader,
  PageLimit,
  Pagination,
  SecondaryButton,
  TableLayout,
} from 'components';
import {roundDownload} from 'assets/images/images';
import {useQuery} from 'react-query';
import {getExportLogs} from 'requests/queries/audit-logs';
import moment from 'moment';
import {parseUrlParams} from 'helpers';
import {useNavigate} from 'react-router';
import FilterV2 from 'components/FilterV2';
import PageNumber from 'components/PageNumber';

function ExportLogs() {
  const navigate = useNavigate();
  const [openFilter, setOpenFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const filterOptions = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: [
        {value: 'completed', label: 'Completed'},
        {value: 'failed', label: 'Failed'},
        {value: 'initialised', label: 'Initiated'},
      ],
      multiSelect: false,
    },
  ];

  const {
    data: response,
    isLoading,
    isError,
    error,
  } = useQuery(['export-logs', currentPage, limit, parseUrlParams()], () =>
    getExportLogs({page: currentPage, limit, ...parseUrlParams()}),
  );

  const export_logs = response?.exports || [];
  const totalPages = response?.totalPages || 0;
  const totalRecords = response?.totalRecords || 0;

  const isFiltered =
    !isLoading &&
    export_logs?.length === 0 &&
    Object.keys(parseUrlParams()).length > 2;

  const handleFilterChange = newFilters => {
    const filteredOptions = {...newFilters};
    if (filteredOptions.dateRange) {
      filteredOptions.start = filteredOptions.dateRange.start;
      filteredOptions.end = filteredOptions.dateRange.end;
      delete filteredOptions.dateRange;
    }
    setCurrentPage(1);
    const searchParams = new URLSearchParams();
    Object.entries(filteredOptions).forEach(([key, value]) => {
      if (value) searchParams.set(key, value);
    });
    navigate(`?${searchParams.toString()}`);
  };

  const handleResetFilter = () => {
    setCurrentPage(1);
    navigate('');
  };

  return (
    <LogsLayout>
      <div className="flex items-center justify-end min-h-[52px] py-2 flex-wrap gap-3 mr-[-24px] ml-[-32px] px-8 border border-transparent border-b-[#E4E4E6]">
        <SecondaryButton
          buttonText="Filter"
          onClick={() => setOpenFilter(prev => !prev)}
        />
      </div>

      <div className={`filter-slide-container ${openFilter ? 'open' : ''}`}>
        <FilterV2 filters={filterOptions} onFilterChange={handleFilterChange} />
      </div>

      {isLoading ? (
        <Loader />
      ) : isError ? (
        <EmptyState
          body={`There was an error fetching the logs: ${error?.message}`}
          noBtn
          height={55}
        />
      ) : !export_logs.length && !isFiltered ? (
        <EmptyState noBtn height={55} body="No logs found" />
      ) : !export_logs.length && isFiltered ? (
        <EmptyState
          body="This filter did not return any data, try using different values."
          noBtn
          height={55}
          customBtn={
            <button
              onClick={handleResetFilter}
              className="text-brandBlue p-4 text-sm font-medium"
            >
              Update preferences
            </button>
          }
        />
      ) : (
        <>
          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                  Source
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  Status
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  Date
                </th>
                <th className="w-[10.69%]"></th>
              </tr>
            </thead>
            <tbody className="text-sm bg-white">
              {export_logs.map(log => (
                <tr key={log._id} className="border-[0.5px] border-[#E4E3EC]">
                  <td className="p-4 pl-4 sm:pl-6 w-[37%] xl:pl-12 text-tiny text-body">
                    {log.type} - {log.env}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap capitalize">
                    <div
                      className={`py-1 px-2   text-xs font-medium uppercase rounded-full w-fit ${
                        log.status === 'failed'
                          ? 'text-[#B42318] bg-[#F0D3D1]'
                          : log.status === 'initialised'
                          ? 'text-white bg-batchBlue'
                          : 'text-[#396C31] bg-[#DDECDB]'
                      }`}
                    >
                      {log.status === 'initialised' ? 'Initiated' : log.status}
                    </div>
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap capitalize">
                    {moment(log?.createdAt).format('Do MMM, YYYY, hh:mmA') ||
                      '-'}
                  </td>
                  <td className="p-4 pl-0">
                    <a
                      href={log.link}
                      download
                      className="flex w-fit items-center gap-2 text-[#0D192C] rounded-lg py-2 px-3 border border-[#12376914] text-xs font-medium  "
                    >
                      Download
                      <img src={roundDownload} alt="download" />
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableLayout>

          {!isLoading && export_logs.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
              <PageNumber
                currentPage={+currentPage}
                limit={limit}
                totalCount={totalRecords}
              />
              <div className="flex items-center gap-5">
                <PageLimit
                  onLimitChange={newLimit => {
                    setLimit(newLimit);
                    setCurrentPage(1);
                  }}
                  total={totalRecords}
                  length={export_logs.length}
                  limit={limit}
                />
                <Pagination
                  total={totalPages}
                  current={currentPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          )}
        </>
      )}
    </LogsLayout>
  );
}

export default ExportLogs;
